import {
    call,
    put,
    select,
    all,
    takeLatest,
    takeEvery
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import * as actionCreators from '../actions/actionCreators';
import { updateFilter } from '../utils/updateFilter';
import { MIN_ZOOM, INITIAL_ZOOM, CENTER_ZOOM, DEFAULT_CENTER } from '../constants/map';

import {
    INIT,
    FETCH_MACHINES,
    FETCH_MACHINES_SUCCESS,
    FETCH_MACHINES_UPDATED,
    FETCH_EXCHANGERATES,
    SET_CENTER_TO_CURRENT_COORDINATES,
    CHECK_DEVICE,
    USER_POSITION_FETCHED,
    WEBSOCKET_CONNECT_AND_LISTEN,
    SET_FILTER,
    SET_CLOSEST_FILTERED_MARKER
} from '../constants/actionTypes';
import { listenForSocketMessages } from './sockets';
import {
    sortByDistance
} from '../utils/sortByDistance';
import { fitBounds } from 'google-map-react/utils';

import {
    getMachines,
    getMachinesUpdated,
    getDistanceMatrixClosestItem
} from '../api';

function* callGetMachines() {
    const result = yield call(getMachines);

    if (result.status === 'ok') {
        yield put(
            actionCreators.fetchMachinesSuccess(result.data, result.timestamp)
        );
    } else {
        yield put(actionCreators.fetchMachinesFailure(result.message));
    }
}

function* callGetMachinesUpdated() {
    const state = yield select();
    const { items, timestamp } = state.map;
    const result = yield call(getMachinesUpdated, timestamp);

    if (result.status === 'ok') {
        const data = result.data;
        const newTimestamp = result.timestamp;
        const ids = data.map(item => item.identifier);
        const updatedItems = items.filter(item =>
            ids.includes(item.identifier)
        );
        const updatedIds = updatedItems.map(item => item.identifier);
        const newIds = ids.filter(id => !updatedIds.includes(id));
        const newItems = data.filter(item => newIds.includes(item.identifier));

        updatedIds.forEach(id => {
            const index = items.findIndex(x => x.identifier === id);
            const indexUpdated = data.findIndex(x => x.identifier === id);
            items[index] = data[indexUpdated];
        });

        const finalItems = items.concat(newItems);

        yield put(
            actionCreators.fetchMachinesUpdatedSuccess(finalItems, newTimestamp)
        );
    } else {
        yield put(actionCreators.fetchMachinesUpdatedFailure(result.message));
    }
}

function* getUserPosition() {
    const state = yield select();
    const externalGeolocation = state.map.externalGeolocation;

    const position = {};

    if (externalGeolocation) {
        window.onExternalLocationSuccess = (lat, lng) => {
            const location = { coords: { latitude: lat, longitude: lng } };
            position.on({ location });
        };
        window.onExternalLocationError = error => position.on({ error });
        window.webkit.messageHandlers['scriptHandler'].postMessage(
            'requestLocation'
        );

        return {
            getLocation: () => new Promise(resolve => (position.on = resolve))
        };
    }

    if (navigator.geolocation) {
        const timeoutId = setTimeout(() => {
            let error = new Error("No choice made by user, time out");
            error.code = 99;  // Custom error
            position.on({ error })
        }, 20000);

        navigator.geolocation.getCurrentPosition(
            location => {
                clearTimeout(timeoutId);
                position.on({ location })
            },
            error => {
                clearTimeout(timeoutId);
                position.on({ error })
            },
            {timeout: 5000}
        );

        return {
            getLocation: () => new Promise(resolve => (position.on = resolve))
        };
    }

    return {
        getLocation: null,
    };
}

function* callGetUserPosition() {
    const urlParams = new URLSearchParams(window.location.search);
    const hasCity = urlParams.has('city');

    const { getLocation } = yield call(getUserPosition);
    const { error, location } = getLocation ? yield call(getLocation) : {};

    if (error || !getLocation) {
        yield put(actionCreators.setUserPosError(error));
        yield put(actionCreators.setCenter(DEFAULT_CENTER, false));
        yield put(actionCreators.setZoom(INITIAL_ZOOM, false));
    } else {
        yield put(
            actionCreators.setUserPos({
                lat: location.coords.latitude,
                lng: location.coords.longitude
            })
        );

        // Only set center/zoom if there's no city specified in url
        if (!hasCity) {
            yield put(
                actionCreators.setCenter({
                    lat: location.coords.latitude,
                    lng: location.coords.longitude
                })
            );
            yield put(
                actionCreators.setZoom(CENTER_ZOOM)
            );
        }
    }

    yield put(
        actionCreators.userPositionFetched()
    );
}

function* geocodeCityAndCenter(city) {
    try {
        const response = yield fetch(
            `/proxy-google.php?service=geocode&address=${encodeURIComponent(city)}&language=sv&region=sv&key=YOUR_GOOGLE_MAP_KEY`
        );
        const json = yield response.json();

        if (json.status.toLowerCase() === 'ok') {
            const bounds = new google.maps.LatLngBounds(
                new google.maps.LatLng(
                    json.results[0].geometry.viewport.southwest.lat,
                    json.results[0].geometry.viewport.southwest.lng
                ),
                new google.maps.LatLng(
                    json.results[0].geometry.viewport.northeast.lat,
                    json.results[0].geometry.viewport.northeast.lng
                )
            );

            const state = yield select();

            setTimeout(() => {
                state.map.map.fitBounds(bounds);
            }, 2000);
        }
    } catch (error) {
        console.error('Geocoding error', error);
    }
}

function* afterFetchMachines() {
    const urlParams = new URLSearchParams(window.location.search);
    const hasCity = urlParams.has('city');

    if (hasCity) {
        const city = urlParams.get('city');
        yield put(actionCreators.setSearchvalue(city));
        yield call(geocodeCityAndCenter, city);
    }

    yield call(callGetUserPosition);

    yield put(actionCreators.userPositionFetched());
}

function* callCheckForQueryParameters() {
    if (!('URLSearchParams' in window)) {
        return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const state = yield select();

    const { userPos } = state.map;

    if (urlParams.has('marker')) {
        const { items } = state.map;
        const marker = items.find(
            item => item.identifier === urlParams.get('marker')
        );
        yield put(actionCreators.setMarker(marker));
        const center = {
            lat: marker.point.coordinates[1],
            lng: marker.point.coordinates[0]
        };
        yield put(actionCreators.setCenter(center, false));
        yield put(actionCreators.setZoom(CENTER_ZOOM, false));
    } else if (urlParams.has('filter')) { // only apply filter if no marker is selected
        const urlFilters = JSON.parse(decodeURIComponent(urlParams.get('filter')));
        const { filter } = state.map;
        let updatedFilter = [...filter];

        Object.keys(urlFilters).forEach((currency) => {
            urlFilters[currency].forEach((denomination) => {
                if (currency === 'deposit') {
                    updatedFilter = [
                        ...updateFilter(
                            updatedFilter,
                            currency
                        )
                    ];
                } else {
                    updatedFilter = [
                        ...updateFilter(
                            updatedFilter,
                            `${currency}_${denomination}`
                        )
                    ];
                }
            });
        });

        yield put(actionCreators.setFilter(updatedFilter));

        if (userPos && urlParams.has('showClosestFiltered') && urlParams.get('showClosestFiltered')) {
            yield put(actionCreators.setClosestFilteredMarker());
        } else {
            yield put(actionCreators.setCenter(DEFAULT_CENTER, false));
            yield put(actionCreators.setZoom(INITIAL_ZOOM, false));
        }
    }
}

function* callSetFilteredItems() {
    const state = yield select();
    const { filter, items } = state.map;

    if(!items || !filter) {
        actionCreators.setFilteredItems([])
    }

    const filteredItems = items.filter(item => {
        let groups = item.groups
            ? item.groups
            : [];

        const identifiers = [
            ...item.services.map(service => service.identifier),
            ...groups
        ];

        return identifiers.includes(filter) && Array.isArray(item.point.coordinates);
    });

    yield put(
        actionCreators.setFilteredItems(filteredItems)
    );
}

function* callSetClosestFilteredMarker() {
    // This drove me insane;
    // The delay here causes the userPos value to reach high enough granularity in decimals.
    // Without it, the closest machine calculation is slightly off, by around ~1-2km!?
    // I have no idea why this is ¯\_(ツ)_/¯
    yield delay(300);

    const state = yield select();
    const { filteredItems, userPos, mapContainerDimensions } = state.map;

    if (!userPos) return;

    // set closest item by naive/simple distance calc. before triggering a call to the dist. matrix api
    // this acts as a fallback and also gives the user a somewhat
    // correct result while waiting for the api call to finish


    const sorted = sortByDistance({
        x: userPos.lng,
        y: userPos.lat
    }, filteredItems);


    const closestNaiveItem = sorted[0];
    const closestNaiveCoords = {
        lat: closestNaiveItem.point.coordinates[1],
        lng: closestNaiveItem.point.coordinates[0]
    };

    const naiveBounds = {
        ne: {
            lat: Math.max(closestNaiveCoords.lat, userPos.lat),
            lng: Math.max(closestNaiveCoords.lng, userPos.lng)
        },
        sw: {
            lat: Math.min(closestNaiveCoords.lat, userPos.lat),
            lng: Math.min(closestNaiveCoords.lng, userPos.lng)
        }
    };

    const { center, zoom } = fitBounds(naiveBounds, mapContainerDimensions);
    yield put(actionCreators.setCenter(center, false));
    yield put(actionCreators.setZoom(zoom));


    // make the api call to get a better/proper dist. calc.
    const result = yield call(getDistanceMatrixClosestItem, userPos, filteredItems);
    if (result.status === 'ok') {
        const { closestItem } = result.data;

        if(!closestItem || !closestItem.point) {
            return;
        }

        const closestCoords = {
            lat: closestItem.point.coordinates[1],
            lng: closestItem.point.coordinates[0]
        };

        const bounds = {
            ne: {
                lat: Math.max(closestCoords.lat, userPos.lat),
                lng: Math.max(closestCoords.lng, userPos.lng)
            },
            sw: {
                lat: Math.min(closestCoords.lat, userPos.lat),
                lng: Math.min(closestCoords.lng, userPos.lng)
            }
        };

        const { center, zoom } = fitBounds(bounds, mapContainerDimensions);

        yield put(actionCreators.setCenter(center, false));
        yield put(actionCreators.setZoom(zoom));
    }
}

function* init() {
    yield put({ type: FETCH_MACHINES });
    yield put({ type: FETCH_EXCHANGERATES });
    yield put({ type: CHECK_DEVICE });
    yield put({ type: WEBSOCKET_CONNECT_AND_LISTEN });
}

export default function* root() {
    // DEBUG
    // uncomment this to log every state change to the console
    // yield takeEvery('*', function* logger(action) {
    //     const state = yield select();
    //     console.log('action', action);
    //     console.log('state after', state);
    // });

    yield all([
        takeLatest(WEBSOCKET_CONNECT_AND_LISTEN, listenForSocketMessages),
        takeLatest(FETCH_MACHINES, callGetMachines),
        takeLatest(FETCH_MACHINES_SUCCESS, afterFetchMachines),
        takeLatest(FETCH_MACHINES_UPDATED, callGetMachinesUpdated),
        takeLatest(SET_CENTER_TO_CURRENT_COORDINATES, callGetUserPosition),
        takeLatest(USER_POSITION_FETCHED, callCheckForQueryParameters),
        takeLatest(SET_FILTER, callSetFilteredItems),
        takeLatest(SET_CLOSEST_FILTERED_MARKER, callSetClosestFilteredMarker),
        takeLatest(INIT, init)
    ]);
}

